import { addWeeks, addMonths, format, differenceInDays } from 'date-fns';

import { StrikesForExpiration } from 'constants/opc';
import { OptionChain } from 'constants/opc';
import { StrategyInputParams } from './types';

export const DEFAULT_STOCK_POSITION = 100;

export function findClosestNumber(numbers: number[], target: number): number {
  if (numbers.length === 0) {
    throw new Error('The list is empty.');
  }

  let closest = numbers[0];
  let smallestDifference = Math.abs(target - closest);

  for (let i = 1; i < numbers.length; i++) {
    const difference = Math.abs(target - numbers[i]);
    if (difference < smallestDifference) {
      smallestDifference = difference;
      closest = numbers[i];
    }
  }

  return closest;
}

/**
 * The listed strike nearest `target` that carries a two-sided or one-sided quote for `side` in
 * EVERY given expiry, or -1 when there is none. A leg priced from an expiry must use a strike that
 * expiry actually lists: reading an absent quote as `|| 0` would give that leg a fabricated $0
 * premium. Calendars pass both expiries so the one strike exists in each; a diagonal's legs pass
 * their own expiry. Strikes spaced differently across expiries (e.g. $1 weeklies against $5
 * monthlies) are the common case this protects.
 */
export function findClosestListedStrike(
  expiries: readonly StrikesForExpiration[],
  target: number,
  side: 'c' | 'p',
): number {
  const [first] = expiries;
  if (!first?.c || !Number.isFinite(target)) return -1;
  const quoted = (expiry: StrikesForExpiration, strike: number) => {
    const quote = expiry.c?.[strike]?.[side];
    return !!quote && ((quote.a ?? 0) > 0 || (quote.b ?? 0) > 0);
  };
  const candidates = Object.keys(first.c)
    .map(Number)
    .filter((strike) => expiries.every((expiry) => quoted(expiry, strike)));
  return candidates.length ? findClosestNumber(candidates, target) : -1;
}

const DISTANCE_INTO_ARRAY = 0.25;
export function getExpirationDateFromArrayIndex(expirationDates: StrategyInputParams['chain']) {
  if (!expirationDates || expirationDates.length === 0) {
    return null;
  }

  const index = Math.floor(DISTANCE_INTO_ARRAY * expirationDates.length);

  return expirationDates[index];
}

export function getExpirationDateRelativeToToday(
  expirationDates: StrategyInputParams['chain'],
  monthsFromToday = 1,
) {
  if (!expirationDates || expirationDates.length === 0) {
    return null;
  }

  const today = new Date();
  const oneMonth = addMonths(today, monthsFromToday);
  const oneMonthTimestamp = oneMonth.getTime();
  const datesWithTimestamps = expirationDates.map((date) => ({
    ...date,
    ts: expirationToDate(date.ey || 0, date.em || 0, date.ed || 0).getTime(),
  }));
  const closestTimestamp = findClosestNumber(
    datesWithTimestamps.map((d) => d.ts),
    oneMonthTimestamp,
  );
  const closestDate = datesWithTimestamps.find((d) => d.ts === closestTimestamp);

  return closestDate || getExpirationDateFromArrayIndex(expirationDates);
}

export function getDefaultExpiration(
  expirationDates: StrategyInputParams['chain'],
  multiplier = 1,
) {
  return getExpirationDateRelativeToToday(expirationDates, multiplier);
}

export function getStrikesAtExpiration(chain: StrategyInputParams['chain'], expirationKey: string) {
  const strikesAtExpiration = chain?.find((s) => s.e === expirationKey);

  return strikesAtExpiration;
}

export function findClosestStrikePrice(
  strikesAtExpiration: StrikesForExpiration,
  spot: number,
): number {
  const strikes = Object.keys(strikesAtExpiration.c || {}).map((strike) => Number(strike));

  if (!strikes || strikes.length === 0) {
    return -1;
  }

  const closestStrike = findClosestNumber(strikes, spot);

  return closestStrike;
}

/**
 * Creates a Date object for options expiration at market close (4 PM ET)
 *
 * Options expire at market close (4 PM Eastern Time), not at midnight.
 * This function creates a Date object that represents the correct expiration time,
 * automatically handling daylight saving time transitions.
 *
 * Key benefits:
 * - More accurate time-to-expiration calculations
 * - Consistent behavior across different times of year (DST/Standard)
 * - Eliminates ~16 hour calculation errors from using midnight
 *
 * @param year - Full year (e.g., 2024)
 * @param month - Month (1-12, not 0-11)
 * @param day - Day of month (1-31)
 * @returns Date object set to 4 PM ET on the specified date, converted to UTC
 */
export function createMarketCloseDate(year: number, month: number, day: number): Date {
  try {
    // Create a date object for the given date
    const baseDate = new Date(year, month - 1, day);

    // Use Intl.DateTimeFormat to check what the UTC offset is for this date in America/New_York
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      timeZoneName: 'longOffset',
    });

    const parts = formatter.formatToParts(baseDate);
    const offsetPart = parts.find((part) => part.type === 'timeZoneName');

    if (offsetPart?.value) {
      // Parse offset like "GMT-5" or "GMT-4"
      const offsetMatch = offsetPart.value.match(/GMT([+-])(\d+)/);
      if (offsetMatch) {
        const sign = offsetMatch[1] === '+' ? 1 : -1;
        const hours = parseInt(offsetMatch[2], 10);
        const offsetHours = sign * hours;

        // Create UTC date for 4 PM ET
        const utcHour = 16 - offsetHours; // Convert 4 PM ET to UTC
        return new Date(Date.UTC(year, month - 1, day, utcHour, 0, 0, 0));
      }
    }

    // Fallback if Intl method doesn't work
    throw new Error('Intl method failed');
  } catch (error) {
    // Use fallback method
    return createMarketCloseDateFallback(year, month, day);
  }
}

/**
 * Fallback method for timezone conversion if Intl methods fail
 */
function createMarketCloseDateFallback(year: number, month: number, day: number): Date {
  // Create a temporary date to check if we're in daylight saving time
  const tempDate = new Date(
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00:00`,
  );

  // Check if the date is in daylight saving time (EDT) or standard time (EST)
  const isDST =
    tempDate.getTimezoneOffset() <
    Math.max(new Date(year, 0, 1).getTimezoneOffset(), new Date(year, 11, 31).getTimezoneOffset());

  // EDT is UTC-4, EST is UTC-5
  const etOffset = isDST ? -4 : -5;
  const utcHour = 16 - etOffset; // 20 for EST, 21 for EDT

  return new Date(Date.UTC(year, month - 1, day, utcHour, 0, 0, 0));
}

/**
 * Updated expirationToDate function that uses market close time
 */
export function expirationToDate(ey: number, em: number, ed: number) {
  return createMarketCloseDate(ey, em, ed);
}

export type StrikeDirection = 'mid' | 'bid' | 'ask';
export type StrategyLegPosition = 'long' | 'short';

export function getStrategyLegStrikeDirection(
  strikeDirection: StrikeDirection | undefined,
  position: StrategyLegPosition,
): StrikeDirection {
  if (strikeDirection) {
    return strikeDirection;
  }

  return position === 'long' ? 'ask' : 'bid';
}

export function getPremium({
  ask,
  bid,
  strikeDirection = 'ask',
}: {
  ask: number;
  bid: number;
  strikeDirection?: StrikeDirection;
}): number {
  if (strikeDirection === 'ask') {
    return ask;
  } else if (strikeDirection === 'bid') {
    return bid;
  } else {
    return parseFloat(((ask + bid) / 2).toFixed(2));
  }
}

type UnitType = 'dollar' | 'percentage';

const strikeUnitDictionary = [
  { spot: 50, unit: 1 },
  { spot: 100, unit: 2 },
  { spot: 1000, unit: 5 },
  { spot: 2000, unit: 10 },
];

interface Params {
  price: number;
  unit: number;
  unitType?: UnitType;
  multiplier?: number;
  percentMultiplier?: number;
}

export const getStrikeDistance = ({
  price,
  unit,
  unitType,
  percentMultiplier = 1,
}: Params): number => {
  if (unitType === 'percentage') {
    return price + price * ((unit * percentMultiplier) / 100);
  }

  for (const { spot: s, unit: u } of strikeUnitDictionary) {
    if (price < s) {
      return price + u * unit;
    }
  }

  // Assume remaining stocks use percentage as a unit
  return price + price * ((unit * percentMultiplier) / 100);
};

export const getNearestExpirationDate = (label: string): string => {
  let date = new Date();

  if (label === '1W') {
    date = addWeeks(date, 1);
  } else if (['1M', '3M', '6M', '1Y'].includes(label)) {
    const monthsToAdd = {
      '1M': 1,
      '3M': 3,
      '6M': 6,
      '1Y': 12,
    }[label] as number;

    date = addMonths(date, monthsToAdd);
  }

  return format(date, 'yyyy-MM-dd');
};

export const findNearestAvailableExpirationDate = (
  label: string,
  chain: OptionChain,
  _ticker: string,
): string => {
  if (
    !chain ||
    !chain[_ticker] ||
    !chain[_ticker]?.entries ||
    chain[_ticker]?.entries?.length === 0
  ) {
    return getNearestExpirationDate(label);
  }

  const targetDate = getNearestExpirationDate(label);
  const targetDateObj = new Date(targetDate);

  // Extract valid expiration dates from the option chain
  const availableDates = chain[_ticker]?.entries?.map((entry) => entry.e || '');
  const validDates = availableDates?.filter((date) => date) || [];

  if (validDates.length === 0) {
    return targetDate;
  }

  validDates.sort((a, b) => {
    const diffA = Math.abs(differenceInDays(new Date(a), targetDateObj));
    const diffB = Math.abs(differenceInDays(new Date(b), targetDateObj));
    return diffA - diffB;
  });

  return validDates[0] || targetDate;
};
